import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { arrow, autoUpdate, computePosition, flip, offset, shift, size, type Placement } from '@floating-ui/react'

import { avoidSiblings } from './hoverPlacement'
import { collectAvoidZones } from './hoverStore'
import { Button } from '../Button'
import type { HoverAnchor, HoverAnchorInput, HoverContent, HoverContentSchema, HoverPlacement, HoverVariant } from './hoverTypes'
import { isContentSchema } from './hoverTypes'

interface HoverBubbleProps {
  open: boolean
  anchor: HoverAnchorInput | null
  content: HoverContent | null
  variant: HoverVariant
  placement: HoverPlacement
  avoidZonesGroup?: string
  avoidZoneExcludeId?: string
  avoidZoneRects?: () => DOMRect[]
  interactive?: boolean
  maxWidth?: number
  offset?: number
  bubbleClassName?: string
  onRequestClose?: () => void
  onPointerEnter?: (event: React.PointerEvent<HTMLDivElement>) => void
  onPointerLeave?: (event: React.PointerEvent<HTMLDivElement>) => void
}

function normaliseAnchor(anchor: HoverAnchorInput): HoverAnchor {
  if (anchor && typeof (anchor as HoverAnchor).getBoundingClientRect === 'function') {
    return anchor as HoverAnchor
  }
  const point = anchor as { x: number; y: number; width?: number; height?: number }
  const w = point.width ?? 1
  const h = point.height ?? 1
  return {
    getBoundingClientRect: () =>
      ({
        x: point.x,
        y: point.y,
        left: point.x,
        top: point.y,
        right: point.x + w,
        bottom: point.y + h,
        width: w,
        height: h,
        toJSON: () => ({}),
      } as DOMRect),
  }
}

function resolvePlacement(p: HoverPlacement): Placement {
  if (p === 'auto') return 'top'
  return p as Placement
}

function renderSchema(schema: HoverContentSchema) {
  return (
    <>
      {schema.kicker ? <div className="hoverBubble__kicker">{schema.kicker}</div> : null}
      <div className="hoverBubble__title">{schema.title}</div>
      {schema.shortcut ? <span className="hoverBubble__kbd">{schema.shortcut}</span> : null}
      {schema.subtitle ? <div className="hoverBubble__subtitle">{schema.subtitle}</div> : null}
      {schema.body ? <div className="hoverBubble__body--free">{schema.body}</div> : null}
      {schema.meta && schema.meta.length > 0 ? (
        <div className="hoverBubble__meta">
          {schema.meta.map((line, index) => (
            <div key={index} className="hoverBubble__metaItem">
              {line}
            </div>
          ))}
        </div>
      ) : null}
      {schema.actions && schema.actions.length > 0 ? (
        <div className="hoverBubble__actions">
          {schema.actions.map((action, index) => {
            const variant = action.tone === 'accent' ? 'secondary' : 'tertiary'
            const tone = action.tone === 'danger' ? 'danger' : undefined
            return (
              <Button
                key={index}
                size="sm"
                variant={variant}
                tone={tone}
                onClick={(event) => {
                  event.stopPropagation()
                  action.onClick()
                }}
              >
                {action.label}
              </Button>
            )
          })}
        </div>
      ) : null}
    </>
  )
}

export function HoverBubble({
  open,
  anchor,
  content,
  variant,
  placement,
  avoidZonesGroup,
  avoidZoneExcludeId,
  avoidZoneRects,
  interactive = false,
  maxWidth,
  offset: offsetDistance,
  bubbleClassName,
  onRequestClose,
  onPointerEnter,
  onPointerLeave,
}: HoverBubbleProps) {
  const bubbleRef = useRef<HTMLDivElement | null>(null)
  const arrowRef = useRef<HTMLSpanElement | null>(null)
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null)
  const [side, setSide] = useState<string | undefined>(undefined)

  const anchorRef = useRef<HoverAnchor | null>(null)
  const anchorReference = useMemo(() => {
    if (!anchor) return null
    return normaliseAnchor(anchor)
  }, [anchor])

  const isOpen = open && Boolean(anchorReference)
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen)
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen)
    if (!isOpen) {
      setPos(null)
      setSide(undefined)
    }
  }

  useLayoutEffect(() => {
    anchorRef.current = anchorReference
    if (!open || !anchorReference) {
      return
    }
    const bubble = bubbleRef.current
    if (!bubble) return

    let cleanup: (() => void) | null = null

    const update = () => {
      if (!bubbleRef.current || !anchorRef.current) return
      const currentBubble = bubbleRef.current
      const currentAnchor = anchorRef.current
      const accentColor =
        variant === 'rich' && isContentSchema(content as HoverContent) && (content as HoverContentSchema).accent
          ? (content as HoverContentSchema).accent
          : undefined
      if (accentColor) currentBubble.style.setProperty('--hover-accent', accentColor)
      else currentBubble.style.removeProperty('--hover-accent')
      if (maxWidth) currentBubble.style.maxWidth = `${maxWidth}px`

      computePosition(currentAnchor, currentBubble, {
        strategy: 'fixed',
        placement: resolvePlacement(placement),
        middleware: [
          offset(offsetDistance ?? (variant === 'label' ? 8 : 12)),
          flip({ padding: 8, fallbackPlacements: ['top', 'bottom', 'right', 'left', 'top-start', 'bottom-start'] }),
          shift({ padding: 8 }),
          avoidSiblings({
            padding: variant === 'rich' ? 14 : 6,
            getRects: () => (avoidZoneRects ? avoidZoneRects() : collectAvoidZones(avoidZonesGroup, avoidZoneExcludeId)),
          }),
          size({
            padding: 12,
            apply({ availableWidth, availableHeight, elements }) {
              elements.floating.style.maxWidth = `${Math.max(180, availableWidth - 8)}px`
              elements.floating.style.maxHeight = `${Math.max(80, availableHeight - 8)}px`
            },
          }),
          arrow({ element: arrowRef.current, padding: 12 }),
        ],
      }).then(({ x, y, placement: resolvedPlacement, middlewareData }) => {
        if (!bubbleRef.current || !anchorRef.current) return
        setPos({ x, y })
        const resolvedSide = resolvedPlacement.split('-')[0]
        setSide(resolvedSide)
        const arrowData = middlewareData.arrow
        if (arrowData) {
          if (arrowData.x != null) {
            bubbleRef.current.style.setProperty('--ax', `${arrowData.x}px`)
          } else {
            bubbleRef.current.style.removeProperty('--ax')
          }
          if (arrowData.y != null) {
            bubbleRef.current.style.setProperty('--ay', `${arrowData.y}px`)
          } else {
            bubbleRef.current.style.removeProperty('--ay')
          }
        }
      })
    }

    update()
    cleanup = autoUpdate(
      anchorReference as unknown as Element,
      bubble,
      update,
      { animationFrame: false }
    )
    return () => {
      cleanup?.()
    }
  }, [open, anchorReference, placement, variant, content, avoidZonesGroup, avoidZoneExcludeId, avoidZoneRects, maxWidth, offsetDistance])

  useEffect(() => {
    if (!open || !interactive) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onRequestClose?.()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, interactive, onRequestClose])

  // Self-close when the anchor is torn out of the DOM (route change, list
  // re-render, popup dismissal). Without this the bubble hangs on the last
  // computed coordinates until something else triggers a state change.
  useEffect(() => {
    if (!open) return
    const anchorEl = anchor instanceof Element ? anchor : null
    if (!anchorEl) return
    const check = () => {
      if (!anchorEl.isConnected) onRequestClose?.()
    }
    const observer = new MutationObserver(check)
    observer.observe(document.body, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [open, anchor, onRequestClose])

  if (typeof document === 'undefined') return null

  return createPortal(
    <>
      <div
        ref={bubbleRef}
        className={`hoverBubble hoverBubble--${variant}${bubbleClassName ? ` ${bubbleClassName}` : ''}`}
        data-open={open ? 'true' : 'false'}
        data-interactive={interactive ? 'true' : 'false'}
        data-side={side}
        aria-hidden={!open ? 'true' : undefined}
        role={interactive ? 'dialog' : 'tooltip'}
        aria-label={
          interactive && isContentSchema(content as HoverContent)
            ? (content as HoverContentSchema).title
            : undefined
        }
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        style={{
          top: pos ? pos.y : -9999,
          left: pos ? pos.x : -9999,
        }}
      >
        <div className="hoverBubble__body">
          {content == null
            ? null
            : isContentSchema(content as HoverContent)
              ? renderSchema(content as HoverContentSchema)
              : (content as React.ReactNode)}
        </div>
        <span className="hoverBubble__arrow" aria-hidden="true" ref={arrowRef} />
      </div>
    </>,
    document.body,
  )
}
